/**
 * useInterviewAudio -- bridges browser audio I/O with the interview WS.
 *
 * Capture path:
 *   getUserMedia(audio) -> AudioWorklet (downsample to 24kHz mono PCM16)
 *   -> base64-encode -> sendAudio(b64)   [send via the WS hook]
 *
 * Playback path:
 *   pushAudio(b64) -> decode PCM16 -> Float32 -> queue on a scheduled
 *   AudioBufferSourceNode chain so deltas play seamlessly.
 *
 * Amplitude:
 *   We also drive an outAmplitude (0..1) value sampled from the playback
 *   buffer so the PersonaBubble can pulse with the AI's voice.
 *
 * This hook is intentionally decoupled from the WebSocket transport so
 * it stays testable -- the room passes in `sendAudio` and calls
 * `pushAudio` on every ai_audio frame.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

const TARGET_RATE = 24_000;
const WORKLET_URL = '/interview-audio-worklet.js';

// AI-voice playback speed-up. The realtime model voices (especially
// the gpt-realtime-2 ones) read at noticeably different cadences
// per voice id, so we let callers pass a per-persona override via
// `voiceSpeed` (defaults to 1.0 = native cadence). The active value
// is held in a ref so we can pick it up inside the audio scheduler
// without re-running effects on every render.
//
// Caveat: this knob ultimately sets AudioBufferSourceNode.playbackRate,
// which does NOT preserve pitch (the voice ends up slightly higher).
// At ~1.1x the pitch shift is audible but small; most listeners
// just register "faster pace". Above ~1.15-1.2x we'd want to switch
// to a pitch-preserving time-stretch (e.g. soundtouchjs in an
// AudioWorklet).
//
// The recording graph taps DOWNSTREAM of these BufferSource nodes
// (see playbackTapRef), so the captured AI audio in the WebM file
// is sped up automatically -- no second knob to keep in sync.
const DEFAULT_AI_PLAYBACK_SPEED = 1.0;

interface UseInterviewAudioArgs {
  stream: MediaStream | null;
  enabled: boolean;
  micOn: boolean;
  sendAudio: (b64: string) => boolean | void;
  /** Per-persona AI-voice playback speed multiplier (e.g. 1.1).
   * Defaults to 1.0 when omitted. Reads via a ref so changes
   * propagate without rebuilding the audio graph. */
  voiceSpeed?: number;
}

export function useInterviewAudio({
  stream,
  enabled,
  micOn,
  sendAudio,
  voiceSpeed,
}: UseInterviewAudioArgs) {
  // Clamp to a sane range so a typo'd backend value (e.g. 11 instead
  // of 1.1) doesn't ship chipmunk audio to candidates.
  const speedRef = useRef<number>(DEFAULT_AI_PLAYBACK_SPEED);
  useEffect(() => {
    const v = typeof voiceSpeed === 'number' && Number.isFinite(voiceSpeed) ? voiceSpeed : DEFAULT_AI_PLAYBACK_SPEED;
    speedRef.current = Math.min(Math.max(v, 0.75), 1.5);
  }, [voiceSpeed]);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const srcNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Playback scheduling
  const playbackCtxRef = useRef<AudioContext | null>(null);
  const playbackHeadRef = useRef(0); // absolute time of next scheduled buffer
  // Every BufferSourceNode we hand to the destination stays referenced
  // here until it ends. On barge-in / response cancel we walk this set
  // and call .stop() on each -- without that, audio that was already
  // scheduled with .start(future_t) keeps playing after the cancel and
  // overlaps with the next response (the classic "AI says two things at
  // once" symptom).
  const scheduledNodesRef = useRef<Set<AudioBufferSourceNode>>(new Set());
  const lastChunkAmpRef = useRef(0);
  // Tap node: all AI audio is routed through this GainNode (gain=1, no-op)
  // so the recorder hook can fan-out to its own mix destination without
  // re-implementing the playback queue.
  const playbackTapRef = useRef<GainNode | null>(null);
  // Re-render when the playback ctx is created so consumers (recorder hook)
  // can pick it up via the returned `aiAudioContext` / `aiAudioTapNode`.
  const [playbackReady, setPlaybackReady] = useState(false);

  const [ready, setReady] = useState(false);
  const [aiAmplitude, setAiAmplitude] = useState(0);

  // ------------------------------------------------------------------
  // CAPTURE
  // ------------------------------------------------------------------

  useEffect(() => {
    if (!enabled || !stream) return;
    let cancelled = false;

    const setup = async () => {
      try {
        // Use the device's native sample rate; the worklet downsamples to 24kHz.
        const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
        audioCtxRef.current = ctx;

        try {
          await ctx.audioWorklet.addModule(WORKLET_URL);
        } catch (e) {
          console.error('audio worklet load failed', e);
          return;
        }

        const node = new AudioWorkletNode(ctx, 'interview-capture', {
          processorOptions: { targetRate: TARGET_RATE },
          numberOfInputs: 1,
          numberOfOutputs: 0,
          channelCount: 1,
          channelCountMode: 'explicit',
          channelInterpretation: 'speakers',
        });
        workletNodeRef.current = node;

        node.port.onmessage = (ev) => {
          if (!micOnRef.current) return; // hard mute on UI toggle
          const pcm = ev.data?.pcm as ArrayBuffer | undefined;
          if (!pcm) return;
          const b64 = arrayBufferToBase64(pcm);
          sendAudio(b64);
        };

        const src = ctx.createMediaStreamSource(stream);
        srcNodeRef.current = src;
        src.connect(node);

        // Eagerly create the playback ctx so the recorder hook can mix AI
        // audio into the recording from the very first frame. Without
        // this, the recorder grabs `aiAudioContext=null` on mount, falls
        // back to a fresh AudioContext, and then can never connect() the
        // AI tap (Web Audio forbids connecting nodes across contexts) ->
        // recordings would only contain the candidate mic, no AI voice.
        ensurePlaybackCtx();

        if (!cancelled) setReady(true);
      } catch (e) {
        console.error('audio capture setup failed', e);
      }
    };
    setup();

    return () => {
      cancelled = true;
      try { srcNodeRef.current?.disconnect(); } catch { /* noop */ }
      try { workletNodeRef.current?.disconnect(); } catch { /* noop */ }
      try { audioCtxRef.current?.close(); } catch { /* noop */ }
      srcNodeRef.current = null;
      workletNodeRef.current = null;
      audioCtxRef.current = null;
      setReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stream, enabled]);

  // Keep a ref so the worklet onmessage closure always sees the latest mic state
  const micOnRef = useRef(micOn);
  useEffect(() => { micOnRef.current = micOn; }, [micOn]);

  // ------------------------------------------------------------------
  // PLAYBACK
  // ------------------------------------------------------------------

  const ensurePlaybackCtx = useCallback((): AudioContext | null => {
    if (playbackCtxRef.current) return playbackCtxRef.current;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: TARGET_RATE,
        latencyHint: 'interactive',
      });
      playbackCtxRef.current = ctx;
      playbackHeadRef.current = ctx.currentTime + 0.04; // ~40ms lead
      const tap = ctx.createGain();
      tap.gain.value = 1;
      tap.connect(ctx.destination);
      playbackTapRef.current = tap;
      // Chrome's autoplay policy: a newly-created AudioContext may start
      // in 'suspended' state if the parent user gesture has already
      // expired. Without an explicit resume() the playback graph won't
      // run -> MediaRecorder would capture silence for the AI side and
      // the candidate would hear nothing through speakers either.
      if (ctx.state === 'suspended') {
        ctx.resume().catch((e) => console.warn('playback ctx resume failed', e));
      }
      setPlaybackReady(true);
      return ctx;
    } catch (e) {
      console.error('playback ctx init failed', e);
      return null;
    }
  }, []);

  const pushAudio = useCallback((b64: string) => {
    const ctx = ensurePlaybackCtx();
    if (!ctx) return;

    let buf: ArrayBuffer;
    try {
      buf = base64ToArrayBuffer(b64);
    } catch (e) {
      console.warn('audio decode failed', e);
      return;
    }

    const int16 = new Int16Array(buf);
    if (int16.length === 0) return;

    // PCM16 -> Float32 [-1, 1]
    const float32 = new Float32Array(int16.length);
    let sumSq = 0;
    for (let i = 0; i < int16.length; i++) {
      const v = int16[i] / 0x8000;
      float32[i] = v;
      sumSq += v * v;
    }
    // Amplitude for the bubble (smoothed)
    const rms = Math.sqrt(sumSq / int16.length);
    const amp = Math.min(1, rms * 3);
    lastChunkAmpRef.current = lastChunkAmpRef.current * 0.6 + amp * 0.4;
    setAiAmplitude(lastChunkAmpRef.current);

    const audioBuffer = ctx.createBuffer(1, float32.length, TARGET_RATE);
    audioBuffer.copyToChannel(float32, 0);

    const node = ctx.createBufferSource();
    node.buffer = audioBuffer;
    // Per-chunk speed snapshot: caller may change voiceSpeed mid-
    // session (e.g. persona swap) but each scheduled chunk locks in
    // the value at scheduling time so playbackHeadRef math stays
    // consistent with what's actually playing.
    const speedNow = speedRef.current;
    node.playbackRate.value = speedNow;
    // Route through the tap so external consumers (recorder) can fan out
    // without inserting themselves between source and destination.
    node.connect(playbackTapRef.current || ctx.destination);

    // Track this node so a barge-in can hard-stop it. Auto-cleanup on
    // natural end keeps the set bounded.
    scheduledNodesRef.current.add(node);
    node.onended = () => {
      scheduledNodesRef.current.delete(node);
    };

    const now = ctx.currentTime;
    const startAt = Math.max(now + 0.005, playbackHeadRef.current);
    node.start(startAt);
    // playbackHeadRef tracks WALL-CLOCK time of the next free slot in
    // the playback queue. With playbackRate=k, this buffer actually
    // takes `duration / k` seconds of wall clock to play -- if we keep
    // adding the natural duration we'd schedule the next chunk too
    // late and the AI voice would develop a growing gap between
    // sentences. Dividing keeps subsequent chunks back-to-back.
    playbackHeadRef.current = startAt + audioBuffer.duration / speedNow;
  }, [ensurePlaybackCtx]);

  // Slowly decay amplitude when no audio is arriving
  useEffect(() => {
    const id = setInterval(() => {
      lastChunkAmpRef.current *= 0.85;
      if (lastChunkAmpRef.current < 0.01) lastChunkAmpRef.current = 0;
      setAiAmplitude(lastChunkAmpRef.current);
    }, 100);
    return () => clearInterval(id);
  }, []);

  // Reset playback head when the AI's turn ends so the next response
  // doesn't accumulate a growing latency drift. Used after a natural
  // `response.done` -- no need to silence the queue, it already drained.
  const resetPlaybackHead = useCallback(() => {
    const ctx = playbackCtxRef.current;
    if (ctx) playbackHeadRef.current = ctx.currentTime + 0.04;
  }, []);

  // How many milliseconds of AI audio are still scheduled to play.
  // Callers use this to tell the server "even though Azure says
  // response.done, the speaker is still emitting for another N ms" so
  // the server keeps its echo guard up and doesn't let speaker->mic
  // bleed wake up Azure VAD (which would otherwise cancel the trailing
  // audio that hasn't been played yet -- the classic 'AI bricht am
  // ende ab, sagt nicht alles aus dem transcript' bug).
  const getBufferedPlaybackMs = useCallback(() => {
    const ctx = playbackCtxRef.current;
    if (!ctx) return 0;
    const remaining = playbackHeadRef.current - ctx.currentTime;
    if (!Number.isFinite(remaining) || remaining <= 0) return 0;
    return Math.round(remaining * 1000);
  }, []);

  // Hard-stop everything currently scheduled. Called on candidate barge-in
  // so the cancelled response stops talking the instant the user starts.
  // Without this, the previously-scheduled BufferSourceNodes keep playing
  // until they end naturally and overlap with the next response -- the
  // "AI says two things at once" bug.
  const stopAiPlayback = useCallback(() => {
    const ctx = playbackCtxRef.current;
    scheduledNodesRef.current.forEach((node) => {
      try {
        node.onended = null;
        node.stop();
      } catch {
        // Already stopped / never started -- both are fine.
      }
      try {
        node.disconnect();
      } catch {
        // ignore
      }
    });
    scheduledNodesRef.current.clear();
    if (ctx) playbackHeadRef.current = ctx.currentTime + 0.04;
    // Force amplitude to zero so the persona bubble doesn't keep pulsing
    // while we wait for the next response to arrive.
    lastChunkAmpRef.current = 0;
    setAiAmplitude(0);
  }, []);

  // Cleanup playback ctx on unmount
  useEffect(() => {
    return () => {
      scheduledNodesRef.current.forEach((node) => {
        try { node.onended = null; node.stop(); } catch { /* ignore */ }
      });
      scheduledNodesRef.current.clear();
      try { playbackTapRef.current?.disconnect(); } catch { /* noop */ }
      playbackTapRef.current = null;
      try { playbackCtxRef.current?.close(); } catch { /* noop */ }
      playbackCtxRef.current = null;
      setPlaybackReady(false);
    };
  }, []);

  return {
    captureReady: ready,
    aiAmplitude,
    pushAudio,
    resetPlaybackHead,
    getBufferedPlaybackMs,
    stopAiPlayback,
    // Exposed so the recorder hook can mix AI playback into the recording.
    // `playbackReady` triggers a re-render when the ctx is lazily created.
    aiAudioContext: playbackReady ? playbackCtxRef.current : null,
    aiAudioTapNode: playbackReady ? playbackTapRef.current : null,
  };
}

// ============================================================
// Base64 helpers (ArrayBuffer <-> base64) -- browser-only
// ============================================================

function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  // Chunked to avoid stack overflow on large buffers
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

function base64ToArrayBuffer(b64: string): ArrayBuffer {
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}
